/**
 * NDA upload control for the RFQ Create Wizard (Certificates & NDA step).
 *
 * Shown only when "NDA required" is ticked in Basic Details. The chosen file
 * is held in wizard state and uploaded as an RFQDocument of type 'nda' right
 * after the RFQ is created, before it is sent — so an NDA-gated RFQ can never
 * reach a supplier without the agreement they are being asked to accept.
 *
 * Before this existed the step only said "you can upload the NDA document
 * after creating the RFQ", nothing ever asked again, and suppliers landed on
 * an NDA screen with no document and no way through.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import FileDropZone from '../common/FileDropZone';

interface NDAUploadCardProps {
  /** The NDA file chosen so far, or null when none has been picked. */
  file: File | null;
  /** Called with the picked file, or null when the user removes it. */
  onChange: (file: File | null) => void;
}

const ACCEPTED = '.pdf,.doc,.docx';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function NDAUploadCard({ file, onChange }: NDAUploadCardProps) {
  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" />
          NDA Required
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Suppliers must accept this Non-Disclosure Agreement before they can view
          the RFQ details or respond. Upload the agreement they will be asked to
          accept — the RFQ cannot be sent without it.
        </p>

        {/* Customer review 11 Sep (drag and drop everywhere): drop or pick the
            NDA here; dropping another file replaces the current one. */}
        <FileDropZone
          accept={ACCEPTED}
          icon={file ? CheckCircle2 : AlertTriangle}
          hint={file
            ? 'PDF or Word document. Drop another file to replace it.'
            : 'No NDA document uploaded. PDF or Word document. Required before sending.'}
          inputTestId="nda-file-input"
          onFiles={(files) => onChange(files[0])}
          data-testid="nda-drop-zone"
        />
        {file && (
          <div className="flex items-center justify-between rounded-lg border bg-white p-3">
            <div className="min-w-0">
              <p className="font-medium text-sm truncate" title={file.name}>{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Remove NDA document"
              onClick={() => onChange(null)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
